"use client";

import dynamic from "next/dynamic";
import { useState } from "react";
import BirthToolFormShell from "@/components/tool-shell/BirthToolFormShell";
import { useDeferredToolActivation } from "@/components/tool-shell/useDeferredToolActivation";

// The Pitra Dosh check embedded in the /pitra-dosha/ article (the #2460
// pattern: the server section imports only this tiny client module, and the
// form, place search, kundali client and result cards load as an async chunk).
// It loads the offer-free PitraDoshCheck, never PitraDoshClient or
// DeferredPitraDoshClient (a second H1 and the calculator header): the
// article's existing report bridge stays its single offer.
//
// Activation is element-scoped: a reader scrolling the article never downloads
// the island; a tap, focus or submit on the form shell does. The shell carries
// the island's own form props (a required birth time), so every frame before
// the island mounts reads the same words. translate="no" covers the result
// cards, which re-render on "Check Another Chart"
// (tests/translate-safe-islands.test.ts).
//
// The engine replies well after the submit tap, outside CLS's input window, and
// the result is taller than the form (cards, rating and "Check Another Chart"),
// so the reply used to push the article down. The wrapper now reserves the
// tallest post-submit state synchronously in the submit event (the #2777
// /nakshatra/ approach), inside the input window, and keeps it for the mount:
// an error or "Check Another Chart" keeps the space, because a shrink would be
// a counted shift too. Natural heights measured 2026-10-22 (next dev +
// Playwright, the engine mocked; 320/360/390/414/480/640/768/1024/1280 wide):
// the form is 565 px on phones and 483 from 640; the tallest state is the
// detected result (three upay lines), 786 at 320, 732 at 360, 678 at 390, 642
// at 414, 624 at 480 and 608 from 640 (592 from 768); the engine-failure
// alert is 633 / 551 and the clear result 433 or less. Hence 830 / 780 from
// 360 / 650 from sm, about 40 px of headroom each.
//
// The engine-failure alert renders inside the form, above its submit, so the
// reserve alone still let it push the submit down (CLS 0.004). From the same
// submit, the form card also takes the height of the form with that alert
// (640 on phones, 560 from sm) and holds the submit at its foot with one 48 px
// height in every state, the spinner's included (the Hindi check's frame,
// PITRA_CHECK_FRAME): the alert fills space already there.
const SUBMIT_RESERVE = [
  "min-h-[830px] min-[360px]:min-h-[780px] sm:min-h-[650px]",
  "[&>div>form]:flex [&>div>form]:min-h-[640px] [&>div>form]:flex-col sm:[&>div>form]:min-h-[560px]",
  "[&>div>form>:last-child]:mt-auto [&>div>form>:last-child]:min-h-12",
].join(" ");

const SHELL_PROPS = {
  language: "en",
  submitLabel: "Check Pitra Dosh",
  requireBirthTime: true,
  showTrustStrip: true,
} as const;

const PitraCheckIsland = dynamic(() => import("@/app/pitra-dosh-calculator/PitraDoshCheck"), {
  ssr: false,
  loading: () => <BirthToolFormShell {...SHELL_PROPS} />,
});

export default function PitraDoshChartCheck() {
  const { ready, activate } = useDeferredToolActivation({ scope: "element" });
  const [reserved, setReserved] = useState(false);

  return (
    <div
      translate="no"
      className={reserved ? SUBMIT_RESERVE : undefined}
      // Capture, so the reserve lands in the submit event itself, before the
      // form's own handler awaits the engine.
      onSubmitCapture={() => setReserved(true)}
    >
      {ready ? (
        <PitraCheckIsland defaultLanguage="en" resultHeadingLevel="h3" requireBirthTime />
      ) : (
        <BirthToolFormShell {...SHELL_PROPS} onActivate={activate} />
      )}
    </div>
  );
}
